import { useState, useEffect, useCallback } from 'react';
import {
  Loader2, RefreshCw, AlertTriangle, AlertOctagon, Info,
  Clock, Filter, Database, ChevronDown, ChevronRight,
} from 'lucide-react';

const PIPELINES = [
  { key: '', label: 'All pipelines' },
  { key: 'pulse', label: 'Pulse' },
  { key: 'pulse_news', label: 'Pulse · News' },
  { key: 'trends_v2', label: 'Trends v2' },
  { key: 'ingest', label: 'Ingestion' },
  { key: 'scheduler', label: 'Scheduler' },
  { key: 'pregen', label: 'Pre-gen' },
  { key: 'other', label: 'Other' },
];

const SEVERITIES = [
  { key: '', label: 'All severities' },
  { key: 'critical', label: 'Critical' },
  { key: 'error', label: 'Error' },
  { key: 'warning', label: 'Warning' },
];

const WINDOWS = [
  { key: 6, label: 'Last 6 h' },
  { key: 24, label: 'Last 24 h' },
  { key: 72, label: 'Last 3 d' },
  { key: 168, label: 'Last 7 d' },
  { key: 336, label: 'Last 14 d' },
];

const severityStyle = (sev) => {
  if (sev === 'critical') return { icon: AlertOctagon, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', pill: 'bg-red-100 text-red-700' };
  if (sev === 'error')    return { icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', pill: 'bg-orange-100 text-orange-700' };
  return { icon: Info, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', pill: 'bg-amber-100 text-amber-700' };
};

export function PipelineErrorsTab() {
  const [errors, setErrors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pipeline, setPipeline] = useState('');
  const [source, setSource] = useState('');
  const [severity, setSeverity] = useState('');
  const [sinceHours, setSinceHours] = useState(72);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('wtf_admin_token');
      const API = process.env.REACT_APP_BACKEND_URL;
      const q = new URLSearchParams({ since_hours: String(sinceHours), limit: '200' });
      if (pipeline) q.set('pipeline', pipeline);
      if (source) q.set('source', source.trim());
      if (severity) q.set('severity', severity);
      const [errRes, sumRes] = await Promise.all([
        fetch(`${API}/api/admin/pipeline-errors?${q.toString()}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/pipeline-errors/summary?hours=${sinceHours}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const errData = await errRes.json();
      const sumData = await sumRes.json();
      setErrors(errData.errors || []);
      setSummary(sumData);
    } catch (e) {
      console.error('Failed to load pipeline errors:', e);
    } finally {
      setLoading(false);
    }
  }, [pipeline, source, severity, sinceHours]);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded((x) => ({ ...x, [id]: !x[id] }));

  return (
    <div data-testid="pipeline-errors-tab" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-gray-900">Pipeline Errors</h3>
          <p className="text-[10px] text-gray-400">Persistent error log · 14-day retention · auto-prunes after</p>
        </div>
        <button onClick={load} disabled={loading} data-testid="errors-refresh-btn"
          className="px-3 py-1.5 text-[10px] font-bold bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 flex items-center gap-1">
          {loading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} Refresh
        </button>
      </div>

      {/* Summary strip */}
      {summary && (
        <div className="grid grid-cols-4 gap-2" data-testid="errors-summary">
          <SummaryStat label="Total errors" value={summary.total || 0} accent="text-gray-900" />
          <SummaryStat label="Critical" value={countBy(summary.by_pipeline, 'critical')} accent="text-red-600" />
          <SummaryStat label="Error" value={countBy(summary.by_pipeline, 'error')} accent="text-orange-600" />
          <SummaryStat label="Warning" value={countBy(summary.by_pipeline, 'warning')} accent="text-amber-600" />
        </div>
      )}

      {/* Worst sources */}
      {summary?.top_sources?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-3" data-testid="errors-top-sources">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-2">Top offenders ({summary.window_hours}h)</p>
          <div className="flex flex-wrap gap-1.5">
            {summary.top_sources.map((s) => (
              <button key={s.source} onClick={() => setSource(s.source)}
                className="text-[10px] px-2 py-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700 flex items-center gap-1">
                <Database size={9} className="opacity-60" />
                <span className="font-semibold">{s.source}</span>
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">{s.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-3 grid grid-cols-4 gap-2" data-testid="errors-filters">
        <FilterSelect label="Pipeline" value={pipeline} onChange={setPipeline} options={PIPELINES} testid="filter-pipeline" />
        <FilterSelect label="Severity" value={severity} onChange={setSeverity} options={SEVERITIES} testid="filter-severity" />
        <FilterSelect label="Window" value={sinceHours} onChange={(v) => setSinceHours(Number(v))} options={WINDOWS} testid="filter-window" />
        <div className="flex flex-col">
          <label className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1">
            <Filter size={9} /> Source
          </label>
          <input
            data-testid="filter-source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="e.g. coingecko"
            className="text-[11px] px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400"
          />
        </div>
      </div>

      {/* Error list */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-gray-300" size={24} /></div>
      ) : errors.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100" data-testid="errors-empty">
          <p className="text-xs text-gray-400">No errors match these filters. 🎉</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="errors-list">
          {errors.map((err) => (
            <ErrorRow key={err.id} err={err} expanded={!!expanded[err.id]} onToggle={() => toggle(err.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function countBy(byPipeline, severity) {
  if (!byPipeline) return 0;
  return byPipeline.filter((r) => r.severity === severity).reduce((a, b) => a + b.count, 0);
}

function SummaryStat({ label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-3">
      <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400">{label}</p>
      <p className={`text-2xl font-black tabular-nums ${accent}`}>{value}</p>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div className="flex flex-col">
      <label className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-1">{label}</label>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-[11px] px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:border-indigo-400 bg-white"
      >
        {options.map((o) => (
          <option key={String(o.key)} value={o.key}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function ErrorRow({ err, expanded, onToggle }) {
  const sty = severityStyle(err.severity);
  const Icon = sty.icon;
  const when = err.logged_at ? new Date(err.logged_at).toLocaleString() : '—';

  return (
    <div data-testid={`error-row-${err.id}`} className={`bg-white rounded-xl border ${sty.border} overflow-hidden`}>
      <button onClick={onToggle} className="w-full text-left p-3 flex items-start gap-3 hover:bg-gray-50">
        <Icon size={14} className={`mt-0.5 flex-shrink-0 ${sty.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase ${sty.pill}`}>{err.severity}</span>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase bg-gray-100 text-gray-700">{err.pipeline}</span>
            <span className="text-[9px] font-semibold text-gray-500">{err.source}</span>
            <span className="text-[9px] text-gray-400 flex items-center gap-1 ml-auto">
              <Clock size={9} /> {when}
            </span>
          </div>
          <p className="text-[11px] text-gray-800 leading-snug">{err.message}</p>
        </div>
        {(err.stacktrace || (err.context && Object.keys(err.context).length > 0))
          ? (expanded ? <ChevronDown size={14} className="text-gray-400 mt-1" /> : <ChevronRight size={14} className="text-gray-400 mt-1" />)
          : null}
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-gray-50">
          {err.context && Object.keys(err.context).length > 0 && (
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mt-2 mb-1">Context</p>
              <pre className="text-[10px] bg-gray-50 p-2 rounded-lg overflow-auto whitespace-pre-wrap">{JSON.stringify(err.context, null, 2)}</pre>
            </div>
          )}
          {err.stacktrace && (
            <div>
              <p className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-1">Stacktrace</p>
              <pre className="text-[10px] bg-gray-50 p-2 rounded-lg overflow-auto whitespace-pre-wrap font-mono">{err.stacktrace}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
